import { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import Navbar from './Navbar';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom, #0f2847, #1e3a5f)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  formContainer: {
    backgroundColor: 'rgba(230, 236, 245, 0.95)',
    padding: '2.5rem',
    borderRadius: '15px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    border: '2px solid rgba(45, 90, 158, 0.15)',
    backdropFilter: 'blur(10px)',
  },
  title: {
    color: '#1a365d',
    fontSize: '2rem',
    marginBottom: '1.5rem',
    textAlign: 'center',
    fontFamily: "'Russo One', sans-serif",
    background: 'linear-gradient(45deg, #1a365d, #2d5a9e)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.2rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    color: '#1a365d',
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'Orbitron', sans-serif",
  },
  input: {
    padding: '0.8rem',
    borderRadius: '8px',
    border: '2px solid #e2e8f0',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: '#000000',
    '&:focus': {
      outline: 'none',
      borderColor: '#2d5a9e',
      boxShadow: '0 0 0 3px rgba(45, 90, 158, 0.1)',
      backgroundColor: '#ffffff',
    },
  },
  button: {
    background: 'linear-gradient(45deg, #2d5a9e, #4a90e2)',
    color: 'white',
    padding: '1rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: "'Orbitron', sans-serif",
    marginTop: '1rem',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  toggleText: {
    textAlign: 'center',
    marginTop: '1rem',
    color: '#4a5568',
    fontSize: '0.9rem',
  },
  toggleButton: {
    background: 'none',
    border: 'none',
    color: '#2d5a9e',
    cursor: 'pointer',
    fontWeight: '600',
    textDecoration: 'underline',
    '&:hover': {
      color: '#1a365d',
    },
  },
  error: {
    color: '#e53e3e',
    fontSize: '0.9rem',
    marginTop: '0.5rem',
    textAlign: 'center',
  },
};

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate('/profile');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (isLogin) {
        // Login
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/profile');
      } else {
        // Sign up
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // Update profile with name
        await updateProfile(userCredential.user, {
          displayName: name
        });

        // Create user document in Firestore
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          name: name,
          email: email,
          uid: userCredential.user.uid,
          createdAt: new Date().toISOString(),
        });

        navigate('/profile');
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError(error.message);
    }
  };

  return (
    <>
      <Navbar currentPage="login" />
      <div style={styles.container}>
        <div style={styles.formContainer}>
          <h1 style={styles.title}>{isLogin ? 'Welcome Back' : 'Join FantasyForge'}</h1>
          <form onSubmit={handleSubmit} style={styles.form}>
            {!isLogin && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>NAME</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={styles.input}
                  placeholder="Enter your name"
                  required
                />
              </div>
            )}
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>EMAIL</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                placeholder="Enter your email"
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>PASSWORD</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                placeholder="Enter your password"
                required
              />
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button type="submit" style={styles.button}>
              {isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div style={styles.toggleText}>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              style={styles.toggleButton}
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login; 